import { useState, useRef } from "react";
import { CalendarDays, Shield, Clock, Plus, ArrowRight, TrendingUp, Activity, Home, FileText, Wrench, ChevronRight, CreditCard, DollarSign, BanknoteIcon, UserPlus, Upload, CheckCircle2, XCircle, AlertCircle, RefreshCw, Link2, Trash2 } from "lucide-react";
import {
  CalendarDots as PhCalendarDots,
  ShieldCheck as PhShieldCheck,
  Clock as PhClock,
  TrendUp as PhTrendUp,
  Pulse as PhPulse,
  FileText as PhFileText,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityFeed } from "@/components/ActivityFeed";
import { useBookings } from "@/hooks/useBookings";
import { useHosts } from "@/hooks/useHosts";
import { useAuth } from "@/contexts/AuthContext";
import { useDocuments, useUploadDocument, useRegistrationCertificate, REGISTRATION_CERT_NAME } from "@/hooks/useDocuments";
import { useNavigate } from "react-router-dom";
import { isToday, isFuture, parseISO, differenceInDays } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useConnectStatus, useCreateConnectAccount, useConnectOnboardingLink } from "@/hooks/useStripeConnect";
import { useAmenityRevenue } from "@/hooks/useAmenities";
import { useOrgRequests } from "@/hooks/useRequests";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIcalFeeds, useAddIcalFeed, useDeleteIcalFeed, type HostIcalFeed } from "@/hooks/useIcalFeeds";
import { useHostBookingConflicts, useResolveBookingConflict } from "@/hooks/useBookingConflicts";

const ICAL_PLATFORM_LABELS: Record<HostIcalFeed["platform"], string> = {
  airbnb: "Airbnb",
  vrbo: "VRBO",
  booking_com: "Booking.com",
  other: "Other",
};

export default function HostDashboard() {
  const { user, assignments, activeOrgId } = useAuth();
  const { data: connectStatus } = useConnectStatus();
  const { data: revenue = [] } = useAmenityRevenue();
  const createConnectAccount = useCreateConnectAccount();
  const getOnboardingLink = useConnectOnboardingLink();

  const totalAmenityRevenue = revenue.reduce((s, r) => s + Number(r.total_revenue), 0);
  const pendingAmenityBookings = revenue.reduce((s, r) => s + Number(r.pending_bookings), 0);

  async function handleConnectStripe() {
    if (!activeOrgId) return;
    if (!connectStatus?.account_id) {
      const result = await createConnectAccount.mutateAsync(activeOrgId);
      if (!result.onboarded) {
        const link = await getOnboardingLink.mutateAsync(activeOrgId);
        if (link.url) window.location.href = link.url;
      }
    } else {
      const link = await getOnboardingLink.mutateAsync(activeOrgId);
      if (link.url) window.location.href = link.url;
    }
  }
  const navigate = useNavigate();
  const { data: hosts = [], isLoading: hostsLoading } = useHosts();
  const { data: bookings = [], isLoading: bookingsLoading } = useBookings();
  const isLoading = hostsLoading || bookingsLoading;

  // Find the current user's host record
  const myHost = hosts.find((h: any) => h.user_id === user?.id);
  const myBookings = myHost ? bookings.filter((b: any) => b.host_id === myHost.id) : [];
  const { data: docs = [] } = useDocuments(myHost?.id);
  const { data: regCert, refetch: refetchCert } = useRegistrationCertificate(myHost?.id);
  const uploadDocument = useUploadDocument();
  const [certUploading, setCertUploading] = useState(false);
  const certInputRef = useRef<HTMLInputElement>(null);

  async function handleCertUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !myHost?.id) return;
    setCertUploading(true);
    uploadDocument.mutate(
      { file, hostId: myHost.id, name: REGISTRATION_CERT_NAME },
      { onSuccess: () => { refetchCert(); setCertUploading(false); if (certInputRef.current) certInputRef.current.value = ""; },
        onError: () => setCertUploading(false) }
    );
  }

  const { data: myRequests = [] } = useOrgRequests();
  const pendingRequests = myRequests.filter((r: any) => r.status === "new" || r.status === "pending");

  const { data: icalFeeds = [] } = useIcalFeeds(myHost?.id);
  const addIcalFeed = useAddIcalFeed(myHost?.id);
  const deleteIcalFeed = useDeleteIcalFeed(myHost?.id);
  const { data: bookingConflicts = [] } = useHostBookingConflicts(myHost?.id);
  const resolveConflict = useResolveBookingConflict();
  const [newFeedPlatform, setNewFeedPlatform] = useState<HostIcalFeed["platform"]>("airbnb");
  const [newFeedUrl, setNewFeedUrl] = useState("");

  function handleAddIcalFeed() {
    if (!newFeedUrl.trim()) return;
    addIcalFeed.mutate(
      { platform: newFeedPlatform, feedUrl: newFeedUrl },
      { onSuccess: () => setNewFeedUrl("") }
    );
  }

  const activeOrgName =
    assignments.find((a) => a.org_id === activeOrgId)?.organization?.name ?? "Your Building";

  const nightsUsed = myHost?.nights_used ?? 0;
  const nightLimit = myHost?.night_limit ?? 90;
  const nightPercent = nightLimit > 0 ? Math.min(Math.round((nightsUsed / nightLimit) * 100), 100) : 0;
  const nightsRemaining = nightLimit - nightsUsed;

  // Today's events
  const today = new Date().toISOString().split("T")[0];
  const checkInsToday = myBookings.filter((b: any) => b.check_in === today && b.status === "approved");
  const checkOutsToday = myBookings.filter((b: any) => b.check_out === today && b.status === "approved");
  const upcomingBookings = myBookings
    .filter((b: any) => b.status === "approved" && isFuture(parseISO(b.check_in)))
    .sort((a: any, b: any) => a.check_in.localeCompare(b.check_in))
    .slice(0, 3);
  const pendingBookings = myBookings.filter((b: any) => b.status === "pending");
  const expiringSoon = myHost?.expires_at
    ? differenceInDays(parseISO(myHost.expires_at), new Date()) <= 30
    : false;

  const nightBarColor =
    nightPercent >= 90 ? "bg-destructive" :
    nightPercent >= 75 ? "bg-amber-500" :
    "bg-primary";

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // ── No host profile yet — show action-oriented "Get Started" screen ──────────
  if (!myHost) {
    const quickActions = [
      {
        icon: Home,
        title: "Add Your First Property",
        description: "Add a unit or property to start tracking bookings and occupancy.",
        cta: "Add Property",
        path: "/units",
        color: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-500/10",
      },
      {
        icon: CalendarDays,
        title: "Create a Booking",
        description: "Log a guest stay once you have a property — track nights and dates.",
        cta: "New Booking",
        path: "/my-bookings",
        color: "text-emerald-600 dark:text-emerald-400",
        bg: "bg-emerald-50 dark:bg-emerald-500/10",
      },
      {
        icon: FileText,
        title: "Upload Documents",
        description: "Store STR permits, insurance certificates, and lease agreements.",
        cta: "Upload Docs",
        path: "/host-documents",
        color: "text-violet-600 dark:text-violet-400",
        bg: "bg-violet-50 dark:bg-violet-500/10",
      },
      {
        icon: Wrench,
        title: "Log a Maintenance Issue",
        description: "Track repairs and open maintenance requests for your properties.",
        cta: "Log Issue",
        path: "/maintenance",
        color: "text-amber-600 dark:text-amber-400",
        bg: "bg-amber-50 dark:bg-amber-500/10",
      },
    ];

    return (
      <div className="space-y-8">
        {/* Welcome header */}
        <div className="text-center py-10 space-y-3">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-4 ring-primary/10 mb-2">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome to Your Host Dashboard
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            You're all set to manage your STR independently. Add a property to start
            tracking bookings, upload compliance documents, and run your guest portal — 
            no invitation needed.
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-sm mx-auto">
            If your building has a strata council or property manager, they can invite
            you to link in for compliance tracking later.
          </p>
        </div>

        {/* Quick action cards */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.title}
                onClick={() => navigate(action.path)}
                className="group text-left bg-card border border-border rounded-2xl p-5 hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${action.bg} mb-3 group-hover:scale-110 transition-transform`}>
                  <Icon className={`h-5 w-5 ${action.color}`} />
                </div>
                <h3 className="font-semibold text-sm text-foreground mb-1">{action.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{action.description}</p>
                <span className={`inline-flex items-center gap-1 text-xs font-medium ${action.color}`}>
                  {action.cta} <ChevronRight className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {myHost?.unit?.unit_number ? `Unit ${(myHost as any).unit.unit_number}` : "No unit assigned"} — {activeOrgName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate("/my-invitations")}>
            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Invite
          </Button>
          <Button size="sm" onClick={() => navigate("/my-bookings")}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New Booking
          </Button>
        </div>
      </div>

      {/* ── Payout setup prompt ── */}
      {!connectStatus?.onboarded && revenue.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <BanknoteIcon className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm">
              <strong>Action needed:</strong> Connect your bank account to receive amenity booking payments from guests.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
              onClick={handleConnectStripe}
              disabled={createConnectAccount.isPending || getOnboardingLink.isPending}
            >
              <CreditCard className="mr-1.5 h-3.5 w-3.5" />
              {createConnectAccount.isPending || getOnboardingLink.isPending ? "Loading…" : "Connect Bank"}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {connectStatus?.onboarded && (totalAmenityRevenue > 0 || pendingAmenityBookings > 0) && (
        <Alert className="border-green-200 bg-green-50">
          <DollarSign className="h-4 w-4 text-green-700" />
          <AlertDescription className="text-green-800 text-sm flex items-center justify-between flex-wrap gap-2">
            <span>
              <strong>Amenity Revenue:</strong> ${totalAmenityRevenue.toFixed(2)} collected
              {pendingAmenityBookings > 0 && ` · ${pendingAmenityBookings} pending booking${pendingAmenityBookings > 1 ? "s" : ""}`}
            </span>
            <Button size="sm" variant="outline" className="shrink-0 border-green-400 text-green-800 hover:bg-green-100"
              onClick={() => navigate("/amenity-management")}>
              View Details
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* ── Expiry warning ── */}
      {expiringSoon && myHost.expires_at && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          <Shield className="h-4 w-4 shrink-0" />
          <span>
            Your STR registration expires in{" "}
            <strong>{differenceInDays(parseISO(myHost.expires_at), new Date())} days</strong>.
            Contact your property manager to renew.
          </span>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Nights Used" value={`${nightsUsed} / ${nightLimit}`} icon={PhCalendarDots} iconWeight="duotone" variant="default" />
        <StatCard title="Active Bookings" value={myBookings.filter((b: any) => b.status === "approved").length} icon={PhCalendarDots} iconWeight="duotone" variant="success" />
        <StatCard title="Registration" value={myHost.status === "approved" ? "Approved" : myHost.status ?? "N/A"} icon={PhShieldCheck} iconWeight="duotone" variant={myHost.status === "approved" ? "success" : "warning"} />
        <StatCard title="Documents" value={docs.length} icon={PhFileText} iconWeight="duotone" variant="info" />
        <StatCard
          title="Pending Requests"
          value={pendingRequests.length}
          icon={PhPulse}
          iconWeight="duotone"
          variant={pendingRequests.length > 0 ? "warning" : "default"}
        />
      </div>

      {/* ── BC STR Registration Certificate ── */}
      <Card className={`border ${
        regCert?.status === "approved" ? "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-800" :
        regCert?.status === "rejected" ? "border-red-200 bg-red-50/50 dark:bg-red-900/10 dark:border-red-800" :
        regCert?.status === "pending"  ? "border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-800" :
        "border-dashed"
      }`}>
        <CardContent className="flex items-center justify-between gap-4 py-4 px-5">
          <div className="flex items-center gap-3 min-w-0">
            {regCert?.status === "approved" && <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />}
            {regCert?.status === "rejected" && <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
            {regCert?.status === "pending"  && <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />}
            {!regCert && <Upload className="h-5 w-5 text-muted-foreground shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-medium">BC STR Registration Certificate</p>
              <p className="text-xs text-muted-foreground truncate">
                {regCert?.status === "approved" && "Verified by your strata admin"}
                {regCert?.status === "rejected" && "Rejected — please upload a new copy"}
                {regCert?.status === "pending"  && `Uploaded ${new Date(regCert.uploaded_at).toLocaleDateString()} · awaiting admin review`}
                {!regCert && "Upload your provincial STR registration certificate for admin verification"}
              </p>
            </div>
          </div>
          {regCert?.status !== "approved" && (
            <>
              <input ref={certInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleCertUpload} />
              <Button size="sm" variant="outline" disabled={certUploading} onClick={() => certInputRef.current?.click()}>
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {certUploading ? "Uploading…" : regCert ? "Replace" : "Upload"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pending guest requests quick-view panel */}
      {pendingRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-amber-600" />
                Guest Requests Awaiting Your Response
              </span>
              <Link to="/requests">
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1">
                  View All <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingRequests.slice(0, 5).map((req: any) => (
              <div key={req.id} className="flex items-center justify-between rounded-md bg-background/70 border px-3 py-2 gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{req.subject}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    From: {req.submitted_by_name ?? req.submitter?.full_name ?? "Guest"} · {req.type}
                  </p>
                </div>
                <Link to="/requests">
                  <Button size="sm" variant="outline" className="h-6 text-[11px] shrink-0">Reply</Button>
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Today at a glance ── */}
      {(checkInsToday.length > 0 || checkOutsToday.length > 0 || pendingBookings.length > 0) && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              Today
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {checkInsToday.map((b: any) => (
              <div key={b.id} className="rounded-md bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 px-3 py-2 text-xs">
                <span className="font-semibold text-green-800 dark:text-green-300">Check-in:</span>{" "}
                <span className="text-green-700 dark:text-green-400">{b.guest_name} ({b.nights}n)</span>
              </div>
            ))}
            {checkOutsToday.map((b: any) => (
              <div key={b.id} className="rounded-md bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 px-3 py-2 text-xs">
                <span className="font-semibold text-blue-800 dark:text-blue-300">Check-out:</span>{" "}
                <span className="text-blue-700 dark:text-blue-400">{b.guest_name}</span>
              </div>
            ))}
            {pendingBookings.length > 0 && (
              <div className="rounded-md bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 px-3 py-2 text-xs">
                <span className="font-semibold text-amber-800 dark:text-amber-300">{pendingBookings.length} pending</span>{" "}
                <span className="text-amber-700 dark:text-amber-400">booking{pendingBookings.length > 1 ? "s" : ""} awaiting approval</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Booking conflicts ── */}
      {bookingConflicts.length > 0 && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              {bookingConflicts.length} Overlapping Booking{bookingConflicts.length > 1 ? "s" : ""} Detected
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Two bookings cover the same dates for the same unit — usually a manual entry
              and a synced calendar event for the same stay. Keep one and cancel the other.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {bookingConflicts.map((c) => (
              <div key={`${c.booking_id}-${c.conflicting_booking_id}`} className="rounded-md border border-destructive/30 bg-background px-3 py-2 space-y-2">
                {[
                  { id: c.booking_id, guest: c.guest_name, checkIn: c.check_in, checkOut: c.check_out, platform: c.platform, synced: c.synced_from_ical },
                  { id: c.conflicting_booking_id, guest: c.conflicting_guest_name, checkIn: c.conflicting_check_in, checkOut: c.conflicting_check_out, platform: c.conflicting_platform, synced: c.conflicting_synced_from_ical },
                ].map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3">
                    <div className="text-xs min-w-0">
                      <span className="font-medium">{b.guest}</span>{" "}
                      <span className="text-muted-foreground">
                        {b.checkIn} → {b.checkOut} {b.synced && `· synced from ${b.platform ?? "calendar"}`}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[11px] shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={() => resolveConflict.mutate(b.id)}
                      disabled={resolveConflict.isPending}
                    >
                      Cancel this one
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── iCal Calendar Sync ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Link2 className="h-4 w-4 text-muted-foreground" />
            Calendar Sync (Airbnb / VRBO)
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Paste your listing's export calendar link to automatically import bookings every hour.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={newFeedPlatform} onValueChange={(v) => setNewFeedPlatform(v as HostIcalFeed["platform"])}>
              <SelectTrigger className="w-32 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ICAL_PLATFORM_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="https://www.airbnb.com/calendar/ical/...ics"
              value={newFeedUrl}
              onChange={(e) => setNewFeedUrl(e.target.value)}
              className="flex-1 min-w-[200px] h-9 text-xs"
            />
            <Button size="sm" onClick={handleAddIcalFeed} disabled={!newFeedUrl.trim() || addIcalFeed.isPending}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Feed
            </Button>
          </div>

          {icalFeeds.length > 0 && (
            <div className="space-y-2">
              {icalFeeds.map((feed) => (
                <div key={feed.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                  <div className="min-w-0 flex items-center gap-2">
                    {feed.last_sync_status === "error" ? (
                      <XCircle className="h-4 w-4 text-destructive shrink-0" />
                    ) : feed.last_synced_at ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    ) : (
                      <RefreshCw className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium">
                        {ICAL_PLATFORM_LABELS[feed.platform]}{" "}
                        <span className="text-muted-foreground font-normal truncate">{feed.feed_url}</span>
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {feed.last_sync_status === "error"
                          ? `Sync failed: ${feed.last_sync_error ?? "Unknown error"}`
                          : feed.last_synced_at
                            ? `Last synced ${new Date(feed.last_synced_at).toLocaleString()} · ${feed.events_synced} booking${feed.events_synced === 1 ? "" : "s"}`
                            : "Not synced yet — runs hourly"}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteIcalFeed.mutate(feed.id)}
                    disabled={deleteIcalFeed.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ── Bookings table ── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">My Bookings</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/my-bookings")}>
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {myBookings.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <CalendarDays className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No bookings yet. Create your first booking.</p>
                <Button size="sm" onClick={() => navigate("/my-bookings")}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> New Booking
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Guest</TableHead>
                    <TableHead className="text-xs">Check-in</TableHead>
                    <TableHead className="text-xs">Nights</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myBookings.slice(0, 6).map((b: any) => (
                    <TableRow key={b.id} className={isToday(parseISO(b.check_in)) ? "bg-primary/5" : ""}>
                      <TableCell className="text-sm font-medium">{b.guest_name}</TableCell>
                      <TableCell className="text-xs">
                        {b.check_in}
                        {isToday(parseISO(b.check_in)) && (
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 border-primary/40 text-primary">Today</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{b.nights}</TableCell>
                      <TableCell><StatusBadge status={b.status || "pending"} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Right column ── */}
        <div className="space-y-4">
          {/* Night usage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Night Usage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Used this year</span>
                <span className="font-medium">{nightsUsed} / {nightLimit}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${nightBarColor}`}
                  style={{ width: `${nightPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className={nightPercent >= 90 ? "text-destructive font-medium" : "text-muted-foreground"}>
                  {nightPercent}% used
                </span>
                <span className="text-muted-foreground">{nightsRemaining} remaining</span>
              </div>
              {nightPercent >= 90 && (
                <p className="text-[11px] text-destructive bg-destructive/5 rounded p-2">
                  You're near your annual night limit. New bookings may be blocked.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming */}
          {upcomingBookings.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Upcoming</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {upcomingBookings.map((b: any) => (
                  <div key={b.id} className="flex justify-between items-center text-xs">
                    <span className="font-medium truncate">{b.guest_name}</span>
                    <span className="text-muted-foreground shrink-0 ml-2">{b.check_in}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Activity feed */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityFeed
                orgId={activeOrgId}
                compact
                limit={5}
                emptyMessage="No recent activity."
                filters={myHost ? { userId: user?.id } : {}}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
